<template>
  <div v-if="editionMode">
    <div class="grid grid-cols-12 gap-1">
      <div
        class="fm-form-input"
        :class="{
          'mt-2': label,
          'col-span-11': showWikiItemButton,
          'col-span-12': !showWikiItemButton
        }"
      >
        <label :for="name"
          >{{ label }}
          <span v-if="required" class="text-red-600">&nbsp;*</span>
        </label>
        <div
          class="flex flex-row items-center rounded-md bg-gray-200 dark:bg-slate-500"
        >
          <div
            class="flex flex-shrink-0 items-center justify-center px-3 text-base"
          >
            <FontAwesomeIcon
              :icon="['fal', icon]"
              fixed-width
              class="text-gray-600 dark:text-white"
            />
          </div>
          <input
            :value="labelText"
            readonly
            class="focus:outline-0"
            @click="isModalOpen = true"
          />
          <button
            type="button"
            class="flex h-8 w-10 flex-shrink-0 items-center justify-center"
            @click="isModalOpen = true"
          >
            <FontAwesomeIcon
              :icon="['fas', placeType ? 'arrows-repeat' : 'arrow-pointer']"
            />
          </button>
          <button
            v-if="placeId"
            type="button"
            class="flex h-8 w-10 flex-shrink-0 items-center justify-center text-red-500 hover:text-red-700"
            @click.stop="handleClear"
          >
            <FontAwesomeIcon :icon="['fas', 'times']" />
          </button>
        </div>
      </div>
      <div v-if="showWikiItemButton" class="col-span-1">
        <BaseButton
          icon="sitemap"
          inverse-icon-order
          class="mt-2"
          color="orange"
          @click="openWikiItemList"
        />
      </div>
    </div>
    <ErrorContainer :error-key="name" :errors="errors" />
  </div>
  <template v-else>
    <BaseShowLabel :label="label" :model-value="labelText">
      <template v-if="placeModel" #append>
        <PlacePopoverComponent :place="placeModel" :type="localPlaceType" />
      </template>
    </BaseShowLabel>
  </template>
  <SelectPlaceModal
    v-model:place-id="localPlaceId"
    v-model:place-type="localPlaceType"
    v-model:place-model="localPlaceModel"
    v-model:place-other="localPlaceOther"
    v-model:place-street="localPlaceStreet"
    v-model:place-extra-street="localPlaceExtraStreet"
    v-model:place-city="localPlaceCity"
    v-model:place-zip="localPlaceZip"
    v-model:place-city-id="localPlaceCityId"
    v-model:place-country="localPlaceCountry"
    v-model:place-city-object="localPlaceCityObject"
    v-model:is-modal-open="isModalOpen"
    :dossier-id="dossierId"
    :latitude="latitude"
    :longitude="longitude"
    :available-place-type="availablePlaceType"
    :suggest-place-of-company="suggestPlaceOfCompany"
    :suggest-place-of-dossier="suggestPlaceOfDossier"
    :default-place-group="defaultPlaceGroup"
    :default-place-type="defaultPlaceType"
    :required-city="requiredCity"
    :event-type="eventType"
  />
  <PlaceWikiItemsModal
    v-if="openWikiItemListModal"
    v-model:is-modal-open="openWikiItemListModal"
    :place-id="placeId"
    :event-id="eventId"
    @close="openWikiItemListModal = false"
  />
</template>

<script>
import SelectPlaceModal from '@ui/modal/place/SelectPlaceModal.vue'
import { PlaceType } from '@/assets/enums'
import PlacePopoverComponent from '@c/addf-package/components/Place/component/PlacePopoverComponent.vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import BaseShowLabel from '@c/addf-package/components/BaseLabel/BaseShowLabel.vue'
import ErrorContainer from '@c/addf-package/components/BaseShowEditInput/ErrorContainer.vue'
import { mapGetters } from 'vuex'
import BaseButton from '@c/addf-package/components/BaseButton/BaseButton.vue'
import PlaceWikiItemsModal from '@c/addf-package/components/Place/component/PlaceWikiItemsModal.vue'

export default {
  name: 'PlaceInput',
  components: {
    PlaceWikiItemsModal,
    BaseButton,
    ErrorContainer,
    BaseShowLabel,
    FontAwesomeIcon,
    PlacePopoverComponent,
    SelectPlaceModal
  },
  props: {
    placeType: {
      type: String,
      required: true
    },
    placeId: {
      type: [String, Number],
      required: true
    },
    placeModel: {
      type: Object,
      required: true
    },
    placeOther: {
      type: String,
      required: false,
      default: null
    },
    placeStreet: {
      type: String,
      required: false,
      default: null
    },
    placeExtraStreet: {
      type: String,
      required: false,
      default: null
    },
    placeZip: {
      type: String,
      required: false,
      default: null
    },
    placeCity: {
      type: String,
      required: false,
      default: null
    },
    placeCityId: {
      type: Number,
      required: false,
      default: null
    },
    placeCityObject: {
      type: Object,
      required: false,
      default: () => {}
    },
    placeCountry: {
      type: String,
      required: false,
      default: null
    },
    dossierId: {
      type: String,
      required: false,
      default: null
    },
    eventId: {
      type: String,
      required: false,
      default: null
    },
    funeralHomeId: {
      type: String,
      required: false,
      default: null
    },
    suggestPlaceOfDossier: {
      type: Boolean,
      required: false,
      default: false
    },
    suggestPlaceOfCompany: {
      type: Boolean,
      required: false,
      default: false
    },
    availablePlaceType: {
      type: Array,
      required: false,
      default: () => Object.values(PlaceType)
    },
    label: {
      type: String,
      required: true
    },
    required: {
      type: Boolean,
      required: false,
      default: false
    },
    requiredCity: {
      type: Boolean,
      required: false,
      default: false
    },
    name: {
      type: String,
      required: true
    },
    errors: {
      type: Array,
      required: false,
      default: null
    },
    icon: {
      type: String,
      required: false,
      default: 'map-marker-alt'
    },
    editionMode: {
      type: Boolean,
      required: false,
      default: false
    },
    defaultPlaceGroup: {
      type: String,
      required: false,
      default: null
    },
    defaultPlaceType: {
      type: String,
      required: false,
      default: null
    },
    eventType: {
      type: String,
      required: false,
      default: null
    }
  },
  emits: [
    'change',
    'clear',
    'update:placeModel',
    'update:placeId',
    'update:placeType',
    'update:placeStreet',
    'update:placeExtraStreet',
    'update:placeCity',
    'update:placeCityObject',
    'update:placeZip',
    'update:placeCountry',
    'update:placeCityId',
    'update:placeOther'
  ],
  data() {
    return {
      isModalOpen: false,
      openWikiItemListModal: false
    }
  },
  computed: {
    ...mapGetters({
      getFuneralHome: 'metadata/getFuneralHomeMetadata',
      countries: 'metadata/getCountriesMetadata',
      dossier: 'dossier/getDossier'
    }),
    labelText() {
      if (
        [PlaceType.SALLE_FUNERARIUM, PlaceType.FUNERARIUM].includes(
          this.placeType
        ) &&
        this.placeType !== 'LDEC'
      ) {
        return (
          this.placeModel?.full_name ||
          this.placeModel?.title?.substr(
            (this.placeModel?.title).indexOf('-') + 1
          )
        )
      } else if ([PlaceType.AUTRES, PlaceType.RUE].includes(this.placeType)) {
        let str_place = [
          this.placeOther,
          this.placeStreet,
          this.placeExtraStreet,
          this.placeCityObject?.zip ?? this.placeZip,
          this.placeCityObject?.name ?? this.placeCity,
          this.countries.find((country) => country.id === this.placeCountry)
            ?.name ?? this.placeCountry
        ]
          .filter((item) => !!item && item.length)
          .join(' - ')

        return str_place === ''
          ? this.$h.capitalizeFirstLetter(this.placeModel?.full_name)
          : this.$h.capitalizeFirstLetter(str_place)
      } else {
        if (this.placeType === 'LDEC') {
          return this.$h.capitalizeFirstLetter(
            `${this.$t('attributes.LDEC')} - ${this.placeModel?.city}`
          )
        } else if (this.placeModel?.city) {
          let data = []
          data.push(
            this.$h.capitalizeFirstLetter(
              this.resolvePlaceName(this.placeModel?.name) || this.placeModel?.title || this.placeModel?.full_name
            )
          )
          data.push(
            this.placeModel.city?.name?.[this.$i18n.locale] ??
              this.placeModel.city
          )
          return data.filter(Boolean).join(' - ')
        } else if (this.placeModel?.address) {
          let data = []
          data.push(
            this.$h.capitalizeFirstLetter(
              this.resolvePlaceName(this.placeModel?.name) || this.placeModel?.title || this.placeModel?.full_name
            )
          )
          return data.filter(Boolean).join(' - ')
        } else {
          return this.$h.capitalizeFirstLetter(
            this.placeModel?.name ?? this.placeModel?.full_name
          )
        }
      }
    },
    funeralHome() {
      if (!this.funeralHomeId) {
        return null
      }
      return this.getFuneralHome(this.funeralHomeId) ?? null
    },
    latitude() {
      if (!this.funeralHome) {
        return null
      }
      return this.funeralHome.address?.latitude ?? null
    },
    longitude() {
      if (!this.funeralHome) {
        return null
      }
      return this.funeralHome.address?.longitude ?? null
    },
    localPlaceId: {
      get() {
        return this.placeId
      },
      set(value) {
        this.$emit('update:placeId', value)
      }
    },
    localPlaceType: {
      get() {
        return this.placeType
      },
      set(value) {
        this.$emit('update:placeType', value)
      }
    },
    localPlaceModel: {
      get() {
        return this.placeModel
      },
      set(value) {
        this.$emit('update:placeModel', value)
      }
    },
    localPlaceOther: {
      get() {
        return this.placeOther
      },
      set(value) {
        this.$emit('update:placeOther', value)
      }
    },
    localPlaceStreet: {
      get() {
        return this.placeStreet
      },
      set(value) {
        this.$emit('update:placeStreet', value)
      }
    },
    localPlaceExtraStreet: {
      get() {
        return this.placeExtraStreet
      },
      set(value) {
        this.$emit('update:placeExtraStreet', value)
      }
    },
    localPlaceCity: {
      get() {
        return this.placeCity
      },
      set(value) {
        this.$emit('update:placeCity', value)
      }
    },
    localPlaceCityId: {
      get() {
        return this.placeCityId
      },
      set(value) {
        this.$emit('update:placeCityId', value)
      }
    },
    localPlaceCityObject: {
      get() {
        return this.placeCityObject
      },
      set(value) {
        this.$emit('update:placeCityObject', value)
      }
    },
    localPlaceZip: {
      get() {
        return this.placeZip
      },
      set(value) {
        this.$emit('update:placeZip', value)
      }
    },
    localPlaceCountry: {
      get() {
        return this.placeCountry
      },
      set(value) {
        this.$emit('update:placeCountry', value)
      }
    },
    wikiPlaces() {
      return [
        PlaceType.CREMATORIUM,
        PlaceType.HOPITAL,
        PlaceType.MAISON_DE_REPOS,
        PlaceType.CIMETIERE,
        PlaceType.CREMATORIUM,
        PlaceType.SALLE_CREMATORIUM,
        PlaceType.ADMINISTRATION_COMMUNALE,
        PlaceType.EGLISE,
        PlaceType.WIKI_FUNERARIUM,
        PlaceType.WIKI_SALLE_CEREMONIE,
        PlaceType.WIKI_SALLE_DE_RECEPTION
      ]
    },
    showWikiItemButton() {
      const routeMatch =
        ([
          'dossier_form_funeral_route_edit',
          'dossier_form_remains_route_edit'
        ].includes(this.$route.name) &&
          this.eventId) ||
        this.$route.name === 'dossier_form_information_route_edit'

      return (
        routeMatch && this.wikiPlaces.includes(this.placeType) && this.placeId
      )
    }
  },
  methods: {
    resolvePlaceName(name) {
      if (!name) return null
      if (typeof name === 'string') return name
      if (typeof name === 'object') {
        return (
          name[this.$i18n.locale] ||
          Object.values(name)
            .find((v) => typeof v === 'string' && v.length > 0) ||
          null
        )
      }
      return null
    },
    openWikiItemList() {
      this.openWikiItemListModal = true
    },
    handleSelectedItem() {},
    handleClear() {
      this.$emit('update:placeId', null)
      this.$emit('update:placeType', null)
      this.$emit('update:placeModel', {})
      this.$emit('clear')
    }
  }
}
</script>
